import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export const PIN_LENGTH = 4;

const cellBorder = (active: boolean, error: boolean) => {
  if (error) return "border-2 border-observe-red";
  if (active) return "border-2 border-observe-primary";
  return "border border-observe-lightest";
};

export interface PinInputProps {
  value: string;
  onChange(value: string): void;
  error?: string;
  disabled?: boolean;
}

export const PinInput: React.FC<PinInputProps> = ({
  value,
  onChange,
  error,
  disabled,
}) => {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!disabled) input.current?.focus();
  }, [disabled]);

  return (
    <div className="flex flex-col gap-2 font-['Open_Sans',sans-serif]">
      <div className="relative flex justify-center gap-3">
        {Array.from({ length: PIN_LENGTH }, (_, i) => {
          const filled = i < value.length;
          const active = !disabled && i === value.length;
          return (
            <div
              key={i}
              data-testid={`pin-cell-${i}`}
              data-filled={String(filled)}
              data-active={String(active)}
              data-error={String(!!error)}
              className={`box-border flex h-14 w-16 items-center justify-center rounded-[5px] border-solid bg-observe-dark ${cellBorder(active, !!error)}`}
            >
              {filled && (
                <span className="h-3 w-3 rounded-full bg-observe-white" />
              )}
            </div>
          );
        })}
        <input
          ref={input}
          type="password"
          inputMode="numeric"
          autoComplete="off"
          maxLength={PIN_LENGTH}
          aria-label={t("PIN")}
          aria-invalid={!!error}
          data-testid="pin-input"
          value={value}
          disabled={disabled}
          onChange={(event) => {
            if (disabled) return;
            const next = event.target.value
              .replace(/\D/g, "")
              .slice(0, PIN_LENGTH);
            if (next !== value) onChange(next);
          }}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-default"
        />
      </div>
      {error && (
        <div role="alert" className="text-right text-xs text-observe-red">
          {error}
        </div>
      )}
    </div>
  );
};
